import { useEffect, useState } from "react";
import EmailGate from "../components/EmailGate";
import { hasStoredContentAccess } from "../lib/gatedContentTracking";

const OFFBOARDING_ACCESS_KEY = "asha_offboarding_guide_access";

const STYLES = `
.guide-doc {
  --paper: #ffffff;
  --paper-2: #f6f9ff;
  --ink: #0f172a;
  --ink-2: #334155;
  --ink-3: #64748b;
  --ink-4: #94a3b8;
  --line: #e2e8f0;
  --line-2: #eef2f7;
  --line-3: #f1f5fb;
  --accent: #2563eb;
  --accent-deep: #1d4ed8;
  --accent-soft: #eff6ff;
  --accent-ink: #1e40af;
  --accent-border: #bfdbfe;

  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--ink);
  font-size: 15.5px;
  line-height: 1.7;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.guide-doc *,
.guide-doc *::before,
.guide-doc *::after { box-sizing: border-box; }

.guide-doc a { color: var(--accent); text-decoration: none; }
.guide-doc a:hover { text-decoration: underline; }

.guide-doc .page {
  max-width: 880px;
  margin: 56px auto;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(15,23,42,0.02),
    0 30px 60px -30px rgba(15,23,42,0.18),
    0 12px 24px -16px rgba(15,23,42,0.10);
  overflow: hidden;
}

/* TOP BAR */
.guide-doc .topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 56px;
  border-bottom: 1px solid var(--line);
  background: var(--paper);
}
.guide-doc .brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.guide-doc .brand-mark {
  width: 26px; height: 26px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}
.guide-doc .brand-mark img { width: 100%; height: 100%; display: block; object-fit: cover; }
.guide-doc .brand-meta { color: var(--ink-3); font-weight: 400; }
.guide-doc .pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent-ink);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid var(--accent-border);
}
.guide-doc .pill::before {
  content: "";
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.18);
}

/* HERO */
.guide-doc .hero {
  padding: 72px 56px 48px;
  border-bottom: 1px solid var(--line);
  position: relative;
  background:
    radial-gradient(900px 280px at 90% -80px, rgba(191, 219, 254, 0.5), transparent 60%),
    radial-gradient(700px 240px at 0% 0%, rgba(239, 246, 255, 0.7), transparent 60%),
    var(--paper);
}
.guide-doc .eyebrow {
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 22px;
  font-weight: 600;
}
.guide-doc .eyebrow b { color: var(--accent-ink); font-weight: 600; }

.guide-doc h1.title {
  font-weight: 700;
  font-size: 56px;
  line-height: 1.04;
  letter-spacing: -0.032em;
  margin: 0 0 18px;
  color: var(--ink);
  max-width: 26ch;
  text-wrap: balance;
}
.guide-doc h1.title em {
  color: var(--accent);
  font-style: normal;
}
.guide-doc .title-sub {
  font-size: 18px;
  color: var(--ink-2);
  margin: 0 0 32px;
  max-width: 42ch;
  font-weight: 400;
}
.guide-doc .lede {
  font-size: 17px;
  line-height: 1.65;
  color: var(--ink-2);
  max-width: 64ch;
  margin: 0 0 40px;
  text-wrap: pretty;
}

.guide-doc .meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.guide-doc .meta-cell {
  padding: 18px 22px 18px 0;
  border-right: 1px solid var(--line-2);
}
.guide-doc .meta-cell:not(:first-child) { padding-left: 22px; }
.guide-doc .meta-cell:last-child { border-right: 0; padding-right: 0; }
.guide-doc .meta-label {
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  margin-bottom: 8px;
  font-weight: 600;
}
.guide-doc .meta-value {
  font-size: 17px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.01em;
}
.guide-doc .meta-value small {
  display: block;
  font-size: 12.5px;
  color: var(--ink-3);
  font-weight: 400;
  margin-top: 4px;
  letter-spacing: 0;
}

/* SECTION */
.guide-doc section.s {
  padding: 56px 56px 60px;
  border-bottom: 1px solid var(--line);
}
.guide-doc section.s:last-of-type { border-bottom: 0; }
.guide-doc .s-eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.guide-doc .s-num {
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent-ink);
  font-weight: 600;
  padding: 3px 9px;
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  border-radius: 5px;
}
.guide-doc .s-rule {
  flex: 1;
  height: 1px;
  background: var(--line);
}
.guide-doc .s-title {
  font-size: 30px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.18;
  color: var(--ink);
  margin: 0 0 14px;
  max-width: 28ch;
  text-wrap: balance;
}
.guide-doc .s-sub {
  font-size: 17px;
  color: var(--ink-2);
  margin: 0 0 28px;
  max-width: 64ch;
  line-height: 1.6;
}

.guide-doc p {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0 0 18px;
  max-width: 68ch;
  text-wrap: pretty;
}
.guide-doc p:last-child { margin-bottom: 0; }
.guide-doc strong { color: var(--ink); font-weight: 600; }
.guide-doc em.accent {
  color: var(--accent-ink);
  background: var(--accent-soft);
  font-style: normal;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 500;
}

.guide-doc h3.sub {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 32px 0 8px;
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.guide-doc h3.sub::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 99px;
  background: var(--accent);
  display: inline-block;
  margin-right: 4px;
  flex-shrink: 0;
}
.guide-doc h3.sub small {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
}

/* Simple lists - prose-style */
.guide-doc ul.simple {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
}
.guide-doc ul.simple li {
  padding: 4px 0 4px 22px;
  position: relative;
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  max-width: 68ch;
}
.guide-doc ul.simple li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  width: 6px; height: 6px;
  border-radius: 99px;
  background: var(--accent);
  opacity: 0.7;
}
.guide-doc ul.simple li b { color: var(--ink); font-weight: 600; }

/* Definition-style list (label — text on a line) */
.guide-doc dl.dl {
  margin: 0 0 22px;
  padding: 0;
  border-top: 1px solid var(--line-2);
}
.guide-doc dl.dl > div {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 28px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line-2);
  align-items: baseline;
}
.guide-doc dl.dl dt {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
}
.guide-doc dl.dl dt small {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent-ink);
  margin-top: 4px;
}
.guide-doc dl.dl dd {
  margin: 0;
  font-size: 15.5px;
  color: var(--ink-2);
  line-height: 1.65;
}

/* Pull quote */
.guide-doc blockquote.pull {
  margin: 28px 0;
  padding: 18px 0 18px 22px;
  border-left: 3px solid var(--accent);
  font-size: 18px;
  font-style: italic;
  color: var(--ink);
  line-height: 1.55;
  max-width: 60ch;
  font-weight: 400;
}

/* Summary table - very minimal, just rules */
.guide-doc table.summary {
  width: 100%;
  border-collapse: collapse;
  margin: 4px 0 0;
}
.guide-doc table.summary thead th {
  text-align: left;
  padding: 0 0 12px;
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-3);
  font-weight: 600;
  border-bottom: 1px solid var(--ink);
}
.guide-doc table.summary tbody td {
  padding: 16px 0;
  border-bottom: 1px solid var(--line-2);
  vertical-align: top;
}
.guide-doc table.summary tbody tr:last-child td { border-bottom: 0; }
.guide-doc table.summary .col-when { width: 32%; padding-right: 24px; }
.guide-doc table.summary .col-detail { width: 68%; }
.guide-doc table.summary .sm-when {
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
}
.guide-doc table.summary .sm-detail {
  font-size: 15px;
  color: var(--ink-2);
  line-height: 1.65;
}

/* FAQ */
.guide-doc .faq-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-top: 1px solid var(--line-2);
  padding-top: 24px;
  margin-top: 4px;
}
.guide-doc .faq-item {
  padding-bottom: 24px;
  border-bottom: 1px dashed var(--line-2);
}
.guide-doc .faq-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.guide-doc .faq-q {
  font-size: 18px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 10px;
  letter-spacing: -0.01em;
  line-height: 1.4;
}
.guide-doc .faq-a {
  font-size: 16px;
  line-height: 1.7;
  color: var(--ink-2);
  margin: 0;
  max-width: 72ch;
}
.guide-doc .faq-a strong { color: var(--ink); font-weight: 600; }

/* Checklist - actionable items in the submission packet */
.guide-doc ul.checklist {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
  max-width: 72ch;
}
.guide-doc ul.checklist li {
  padding: 11px 0 11px 32px;
  position: relative;
  font-size: 15.5px;
  line-height: 1.65;
  color: var(--ink-2);
  border-bottom: 1px dashed var(--line-2);
}
.guide-doc ul.checklist li:last-child { border-bottom: 0; }
.guide-doc ul.checklist li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  width: 17px;
  height: 17px;
  border: 1.5px solid var(--accent-border);
  border-radius: 4px;
  background: var(--paper);
  box-shadow: inset 0 0 0 2px var(--paper);
}
.guide-doc ul.checklist li b { color: var(--ink); font-weight: 600; }

/* Callout - what happens after packet submission */
.guide-doc .callout {
  background: var(--accent-soft);
  border: 1px solid var(--accent-border);
  border-radius: 12px;
  padding: 22px 26px;
  margin: 32px 0 0;
  max-width: 72ch;
}
.guide-doc .callout-eyebrow {
  font-size: 10.5px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent-ink);
  font-weight: 600;
  margin-bottom: 12px;
}
.guide-doc .callout p {
  margin: 0;
  font-size: 15.5px;
  color: var(--ink);
  line-height: 1.7;
  max-width: none;
}
.guide-doc .callout p + p { margin-top: 12px; }
.guide-doc .callout p strong { color: var(--ink); font-weight: 600; }

/* Tail */
.guide-doc .tail {
  padding: 22px 56px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--paper-2);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  border-top: 1px solid var(--line);
  font-weight: 500;
}

/* Responsive */
@media (max-width: 820px) {
  .guide-doc .page { margin: 24px; }
  .guide-doc .hero,
  .guide-doc section.s,
  .guide-doc .topbar,
  .guide-doc .tail { padding-left: 28px; padding-right: 28px; }
  .guide-doc h1.title { font-size: 36px; max-width: none; }
  .guide-doc .s-title { font-size: 24px; }
  .guide-doc .meta-grid { grid-template-columns: 1fr 1fr; }
  .guide-doc .meta-cell { padding: 16px 0 !important; border-right: 0; border-bottom: 1px solid var(--line-2); }
  .guide-doc .meta-cell:nth-child(2n) { padding-left: 22px !important; border-left: 1px solid var(--line-2); }
  .guide-doc .meta-cell:nth-last-child(-n+2) { border-bottom: 0; }
  .guide-doc dl.dl > div { grid-template-columns: 1fr; gap: 4px; }
}

@media (max-width: 560px) {
  .guide-doc .page { margin: 14px; border-radius: 14px; }
  .guide-doc .hero,
  .guide-doc section.s,
  .guide-doc .topbar,
  .guide-doc .tail { padding-left: 22px; padding-right: 22px; }
  .guide-doc .topbar { padding-top: 14px; padding-bottom: 14px; }
  .guide-doc .hero { padding-top: 44px; padding-bottom: 36px; }
  .guide-doc section.s { padding-top: 40px; padding-bottom: 44px; }
  .guide-doc h1.title { font-size: 30px; }
  .guide-doc .lede { font-size: 15.5px; }
  .guide-doc .meta-grid { grid-template-columns: 1fr; }
  .guide-doc .meta-cell { padding-left: 0 !important; border-left: 0 !important; }
  .guide-doc .meta-cell:not(:last-child) { border-bottom: 1px solid var(--line-2); }
  .guide-doc table.summary,
  .guide-doc table.summary tbody,
  .guide-doc table.summary tr,
  .guide-doc table.summary td { display: block; width: auto !important; }
  .guide-doc table.summary thead { display: none; }
  .guide-doc table.summary tr {
    padding: 16px 0;
    border-bottom: 1px solid var(--line-2);
  }
  .guide-doc table.summary tbody tr:last-child { border-bottom: 0; }
  .guide-doc table.summary td { padding: 0 !important; border: 0 !important; }
  .guide-doc table.summary td + td { margin-top: 6px; }
  .guide-doc .tail {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-top: 16px; padding-bottom: 16px;
  }
}

@media print {
  @page { size: letter; margin: 0.4in; }
  body, html { background: #fff !important; }
  body.guide-body { background: #fff !important; }
  .guide-doc { font-size: 11.5px !important; }
  .guide-doc .page {
    max-width: 100% !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  .guide-doc .topbar { padding: 14px 24px; }
  .guide-doc .hero { padding: 32px 24px 24px; }
  .guide-doc h1.title { font-size: 28px; max-width: none; }
  .guide-doc .lede { font-size: 12.5px; }
  .guide-doc section.s { padding: 24px 24px 28px; }
  .guide-doc .s-title { font-size: 18px; }
  .guide-doc .tail { padding: 12px 24px; }
  .guide-doc h3.sub, .guide-doc .s-title, .guide-doc h1.title {
    break-after: avoid;
    page-break-after: avoid;
  }
  .guide-doc dl.dl > div, .guide-doc table.summary tr, .guide-doc blockquote.pull {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

const summary = [
  {
    when: "Before notice",
    detail:
      "Talk to your Account Manager. Most concerns trace back to communication gaps — file tickets on each outstanding issue first so we can resolve them before you commit to a full migration.",
  },
  {
    when: "Vendor selection",
    detail:
      "Sign contracts with the replacement vendors you'll need: new VoIP provider, new payment processor, new fax provider, new patient-messaging provider, and (if migrating off the Asha PMS) a new practice management system.",
  },
  {
    when: "Migrations in progress",
    detail:
      "Each replacement vendor performs the migration off Asha — phone port-outs, payment terminal re-provisioning, fax port-outs, patient-messaging number port-outs, payer-by-payer ERA re-enrollments, and PMS data import.",
  },
  {
    when: "Hardware returns",
    detail:
      "Ship Asha-provided phones and payment terminals back to us once the replacement equipment is live in your office.",
  },
  {
    when: "Submission packet",
    detail:
      "Assemble the off-boarding submission packet (see § 02) — proof of every migration, hardware tracking, support-ticket history, and a signed off-boarding request from your authorized signatory — and send it to your Account Manager. The AM reviews for completeness and against our off-boarding guidelines (typically within 5 business days) and either accepts the packet or returns it for missing items.",
  },
  {
    when: "Off-boarding call",
    detail:
      "Once the packet is accepted, your AM schedules the mandatory off-boarding (uninstallation) call with the practice owner. This is the structured technical handoff where Asha safely decouples integrations from your environment.",
  },
  {
    when: "Cancellation notice + fees",
    detail:
      "Once the call is complete, your formal cancellation notice is submitted and your contractual notice period (30 days unless your SOW says longer) begins. Final fees — Early Termination Fee, Discount Clawback, off-boarding administrative fee, partial-period Monthly Retainer through the cancellation effective date — follow the terms in your SOW (see § 08).",
  },
];

const OffboardingGuidePage = () => {
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    if (
      hasStoredContentAccess(
        OFFBOARDING_ACCESS_KEY,
        "asha_offboarding_guide_session_id"
      )
    ) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(OFFBOARDING_ACCESS_KEY, "true");
    }
    // eslint-disable-next-line no-console
    console.log("Offboarding guide access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    document.body.style.background = "#eef3fb";
    document.body.style.margin = "0";
    document.body.classList.add("guide-body");
    document.title = "Asha — Client Off-Boarding Guide";

    return () => {
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("guide-body");
    };
  }, []);

  return (
    <>
      {isLocked && (
        <EmailGate
          onAccessGranted={handleAccessGranted}
          title="Please enter your email to view the off-boarding guide"
          description={
            <>
              Access to the{" "}
              <span className="text-blue-400 font-semibold">Asha Health</span>{" "}
              client off-boarding guide is reserved for our customers and their
              authorized representatives.
            </>
          }
          submitLabel="View guide"
          agreementNoun="this guide"
          confidentialityText="The information in this guide is intended for existing Asha Health customers. Sharing without prior written consent from Asha Health, Inc. is prohibited."
          sessionStorageKey="asha_offboarding_guide_session_id"
        />
      )}

      <div className="guide-doc">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />

        <div className="page">
        {/* TOP BAR */}
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark">
              <img src="/assets/alogo.png" alt="Asha" />
            </div>
            <span>Asha Health</span>
            <span className="brand-meta">· asha.dental</span>
          </div>
          <span className="pill">Client Off-Boarding</span>
        </div>

        {/* HERO */}
        <div className="hero">
          <div className="eyebrow">
            <b>Off-Boarding Guide</b> &nbsp;·&nbsp; Migrating Away from the
            Asha AI Operating System
          </div>
          <h1 className="title">
            Off-boarding from the <em>Asha AI Operating System</em>
          </h1>
          <p className="title-sub">
            What it takes — and what to expect — when migrating off of Asha.
          </p>
          <p className="lede">
            This guide walks through every workstream that has to be completed
            before your Asha services can be cancelled — phones, payments,
            faxes, patient messaging, payer ERAs, PMS data export, and the
            mandatory uninstallation meeting — and who's responsible for each
            piece. Before you start, please read the section on talking to your
            Account Manager: most off-boarding decisions are reversible if we
            simply hear what's not working.
          </p>

          <div className="meta-grid">
            <div className="meta-cell">
              <div className="meta-label">Migrations driven by</div>
              <div className="meta-value">
                Your incoming vendors
                <small>not Asha — see § 03</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Notice period</div>
              <div className="meta-value">
                30+ days
                <small>per your SOW, starts after the off-boarding call</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">PMS data export</div>
              <div className="meta-value">
                ~7 days
                <small>to generate after written request</small>
              </div>
            </div>
            <div className="meta-cell">
              <div className="meta-label">Prepared by</div>
              <div className="meta-value">
                Asha Health, Inc.
                <small>sales@asha.health</small>
              </div>
            </div>
          </div>
        </div>

        {/* § 01 — TALK TO YOUR AM FIRST */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 01</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Before you off-board, talk to us</h2>
          <p className="s-sub">
            Going through a full setup process again — at a new vendor — is a
            significant undertaking. Almost every issue we've ever seen has been
            resolvable.
          </p>

          <p>
            Onboarding to any dental operating system is a months-long
            workstream: phones, payments, faxes, ERAs, PMS data, and staff
            training. You went through it once with us. Doing it again with a
            new vendor means going through{" "}
            <em className="accent">all of it again</em> — but this time without
            Asha doing the heavy lifting on your behalf (see § 03).
          </p>

          <p>
            And the migration work itself is not a quick task. Coordinating
            port-outs with a new VoIP provider, swapping payment terminals
            with a new processor, signing fax and patient-messaging contracts,
            running a full payer-by-payer ERA re-enrollment, and a PMS data
            import all land on your team's plate at the same time — in
            parallel with running the practice day-to-day. Realistically, this
            is{" "}
            <strong>
              weeks to months of work across your team and your incoming
              vendors
            </strong>
            , on top of the cost of those new vendors themselves.
          </p>

          <p>
            Before you commit to that,{" "}
            <strong>tell us exactly what's bugging you first</strong> — the
            specific issues, the specific moments where Asha didn't deliver,
            the specific workflows that aren't working for your front desk or
            providers. In our experience, problems almost always stem from{" "}
            <strong>not communicating what you're experiencing</strong> to our
            team often enough or in enough detail. When you do bring us the
            specifics, our team is{" "}
            <em className="accent">
              intent on providing a five-star experience in fixing the issue
            </em>{" "}
            — and we usually close the loop in days, not weeks.
          </p>

          <p>The right path before cancellation is:</p>

          <ul className="simple">
            <li>
              <b>File a support ticket for every issue you're seeing</b> — one
              per issue, with as much specificity as you can. Tickets create the
              paper trail we need to investigate and resolve.
            </li>
            <li>
              <b>Bring those tickets to your AM call</b> so we can prioritize
              them and assign owners on our side.
            </li>
            <li>
              <b>Give us a fair window to resolve</b> — our team is responsive
              and most issues close out within days once we know about them.
            </li>
          </ul>

          <blockquote className="pull">
            The vast majority of off-boarding conversations end with us fixing
            what wasn't working. Talk to your Account Manager before you talk to
            another vendor.
          </blockquote>
        </section>

        {/* § 02 — SUBMISSION PACKET CHECKLIST */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 02</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">The off-boarding submission packet</h2>
          <p className="s-sub">
            Before we can formally begin off-boarding, you must assemble and
            submit a complete packet covering every item below. Once the packet
            is received, your Account Manager reviews it for completeness and
            schedules the mandatory uninstallation call.
          </p>

          <p>
            The packet exists so we can verify that every dependent system has
            been cleanly migrated <em className="accent">before</em> we decouple
            Asha from your environment — preventing dropped calls, declined
            payments, lost faxes, missed text messages, denied claims, and
            orphaned PMS data. Incomplete packets are returned for completion,
            and review only restarts once the missing items are submitted.
          </p>

          <p>
            <strong>
              Your billing keeps accruing throughout the migration window.
            </strong>{" "}
            Until every migration is complete, Asha continues to run the full
            platform on your behalf — we can't selectively decommission phones,
            payments, ERAs, or PMS without risking dropped calls, declined
            payments, denied claims, or lost patient data. The entire stack
            stays live, the underlying telephony, processing, clearinghouse,
            and infrastructure costs continue to accrue on our side, and your
            monthly billing continues to accrue normally on yours.
          </p>

          <p>
            <strong>
              Compliance keeps systems active until cutover is confirmed.
            </strong>{" "}
            Until your incoming vendors have confirmed in writing that every
            system has been cleanly migrated and{" "}
            <em className="accent">
              no live traffic is still routing to Asha
            </em>
            , we are required — for compliance and data-integrity reasons — to
            keep all of those systems fully active. Partial migrations don't
            reduce your billing; they extend the window during which billing
            continues to accrue.
          </p>

          <h3 className="sub">
            Communications migrations <small>Proof of completion</small>
          </h3>
          <ul className="checklist">
            <li>
              <b>
                All phone numbers fully ported off Asha and live on your new
                VoIP provider.
              </b>{" "}
              Not "port submitted" or "cutover scheduled" —{" "}
              <em className="accent">
                every phone line you have with Asha has completed the port
              </em>
              , and you're already taking inbound and outbound calls on the new
              vendor's stack. Include the cutover date and the new vendor's
              port-completion confirmation for every line.
            </li>
            <li>
              <b>
                All in-office payment terminals fully migrated and live on your
                new processor.
              </b>{" "}
              Not "terminals shipped" or "swap-out scheduled" —{" "}
              <em className="accent">
                Asha-provided terminals are no longer processing transactions
              </em>{" "}
              and the replacement terminals are accepting live patient payments
              in production. Include written confirmation from your new payment
              processor.
            </li>
            <li>
              <b>
                Fax number(s) fully ported off Asha and live on your new fax
                provider.
              </b>{" "}
              Not "port submitted" — fully cut over, with the new provider
              sending and receiving faxes on your behalf. Include
              port-completion confirmation.
            </li>
            <li>
              <b>
                Patient-messaging / SMS number(s) fully ported off Asha and
                live on your new patient-messaging provider.
              </b>{" "}
              Not "port submitted" — your front desk is actively texting
              patients through the new vendor. Include port-completion
              confirmation, plus a note on how messaging history is being
              preserved.
            </li>
          </ul>

          <h3 className="sub">
            ERA re-enrollment <small>Payer-by-payer</small>
          </h3>
          <ul className="checklist">
            <li>
              <b>
                Every payer's ERAs already flowing to the new destination.
              </b>{" "}
              Not "enrollment submitted" or "pending payer setup" —{" "}
              <em className="accent">
                for every payer on your roster, ERAs are actively landing at
                the new clearinghouse, the new vendor, or the paper-EOB inbox
              </em>{" "}
              you've designated. Include a spreadsheet of every payer with the
              destination, the confirmation reference number from each payer,
              and the date of the most recent ERA received at the new
              destination.
            </li>
            <li>
              <b>
                No payer — primary, secondary, or low-volume tail — is still
                delivering ERAs to Asha.
              </b>{" "}
              Per TOS § 11.3(j), cancellation cannot take effect while any
              single payer is still routing ERAs to us. Stragglers (low-volume
              payers and secondaries that get forgotten) are the most common
              reason packets are returned for completion.
            </li>
          </ul>

          <h3 className="sub">
            PMS data <small>Skip on Continuous Integration</small>
          </h3>
          <p>
            <strong>
              This section only applies if you're currently on the Asha PMS
              and migrating to a different PMS.
            </strong>{" "}
            If you're on our{" "}
            <em className="accent">Continuous Integration</em> model — where
            Asha's AI Agents run on top of an existing third-party PMS like
            Open Dental, Eaglesoft, or Dentrix — this section doesn't apply.
            Your PMS isn't changing, so there's no Asha data to export and no
            new PMS to ingest into. Skip ahead to the next section; the rest
            of the packet still applies to you.
          </p>
          <ul className="checklist">
            <li>
              <b>
                Asha-generated PMS data export already delivered to your new
                PMS vendor.
              </b>{" "}
              Submit your written data-export request to Asha at least{" "}
              <strong>14 days</strong> before packet submission so the export
              has time to generate (~7 days), reach the new vendor, and be
              ingested and validated{" "}
              <em className="accent">before</em> you assemble the packet.
            </li>
            <li>
              <b>New PMS already live as your system of record.</b> Not "data
              imported, going live next week" —{" "}
              <em className="accent">
                your front desk and providers are actively using the new PMS
                day-to-day
              </em>
              , with written confirmation from your new PMS vendor that the
              export was received, imported, and validated.
            </li>
          </ul>

          <h3 className="sub">
            Hardware return <small>Tracking required</small>
          </h3>
          <ul className="checklist">
            <li>
              <b>Asha-provided phones boxed and shipped.</b> Carrier and
              tracking number included in the packet.
            </li>
            <li>
              <b>Asha-provided payment terminals boxed and shipped.</b> Carrier
              and tracking number included in the packet.
            </li>
            <li>
              <b>Any other Asha-provided hardware accounted for.</b> Listed in
              the packet with current status (in transit, delivered,
              retained-for-rebuild, etc.).
            </li>
          </ul>

          <h3 className="sub">
            Account Manager engagement <small>Issue history</small>
          </h3>
          <ul className="checklist">
            <li>
              <b>List of every support ticket filed</b> for the issues that
              drove the off-boarding decision — ticket numbers, dates, and
              current status.
            </li>
            <li>
              <b>Notes from your AM resolution conversation</b> — what was
              tried, what was resolved, and what specifically remained
              unresolved at the point you chose to off-board.
            </li>
          </ul>

          <h3 className="sub">
            Authorization <small>Signatory + practice owner</small>
          </h3>
          <ul className="checklist">
            <li>
              <b>Practice owner or authorized officer named</b> for the
              off-boarding call — full name, title, and authority
              confirmation.
            </li>
            <li>
              <b>Signed off-boarding request from the authorized signatory</b>{" "}
              on your SOW — confirming the practice's intent to off-board
              pending successful completion of the off-boarding call. (The
              formal cancellation notice itself is submitted{" "}
              <em className="accent">after</em> the call, not as part of this
              packet.)
            </li>
          </ul>

          <h3 className="sub">
            Multi-location groups <small>Only if applicable</small>
          </h3>
          <ul className="checklist">
            <li>
              <b>
                Every location in your group has completed every item above.
              </b>{" "}
              Per TOS § 11.3(g), partial off-boarding isn't permitted — the
              packet must cover every location in your multi-location group
              before review can begin.
            </li>
          </ul>

          <div className="callout">
            <div className="callout-eyebrow">
              What happens after you submit the packet
            </div>
            <p>
              Send the assembled packet to your Account Manager as a single PDF
              or zipped folder with each item clearly labeled. From there, the
              sequence is fixed:
            </p>
            <p>
              <strong>1. Your AM reviews the packet</strong> — typically within{" "}
              <strong>5 business days</strong> — for completeness and against
              our off-boarding guidelines. If items are missing or don't meet
              the "completed, not submitted" standard above, the packet is
              returned with a list of outstanding items and the review clock
              restarts once those items are re-submitted.
            </p>
            <p>
              <strong>
                2. Once the packet is accepted as satisfactory, your AM
                schedules the off-boarding call
              </strong>{" "}
              with the practice owner or authorized officer — the structured
              technical handoff where we safely decouple Asha from your
              environment (see § 06 for what's covered).
            </p>
            <p>
              <strong>
                3. Once the off-boarding call is complete, your formal
                cancellation notice is submitted
              </strong>{" "}
              and your contractual notice period — 30 days unless your SOW
              specifies longer — begins from that point.
            </p>
            <p>
              <strong>
                4. Final cancellation fees, if any, are calculated per your
                contract.
              </strong>{" "}
              Any Early Termination Fee, Discount Clawback, off-boarding
              administrative fee, and the partial-period Monthly Retainer
              through the cancellation effective date follow the terms in your
              SOW. See § 08 for the plain-English summary of what may apply.
            </p>
          </div>
        </section>

        {/* § 03 — WHO DOES THE WORK */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 03</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Who does the migration work</h2>
          <p className="s-sub">
            This is the single most important thing to understand about
            off-boarding: the direction of migration determines who does the
            work.
          </p>

          <p>
            When you <strong>migrated onto Asha</strong>, we did the work.
            Weave didn't port your phones onto us — we did. Open Dental didn't
            move your PMS data onto us — we did. Your payers didn't re-route
            their ERAs onto us — we did. Whichever direction a customer is
            moving, <em className="accent">the receiving platform</em> is the
            one that runs the migration, because it's the side with the
            incentive and the implementation team.
          </p>

          <p>
            When you <strong>migrate off of Asha</strong>, the same principle
            applies in reverse:
          </p>

          <ul className="simple">
            <li>
              <b>Your new VoIP provider</b> is responsible for porting your
              phone lines off Asha — not us.
            </li>
            <li>
              <b>Your new payment processor</b> is responsible for migrating
              your payment terminals off Asha — not us.
            </li>
            <li>
              <b>Your new fax provider</b> is responsible for porting your fax
              line off Asha — not us.
            </li>
            <li>
              <b>Your new patient-messaging provider</b> is responsible for
              porting your texting number off Asha — not us.
            </li>
            <li>
              <b>Each payer</b> is responsible — at your request — for
              re-routing your ERAs to your new clearinghouse or back to paper
              EOBs. You contact them; we don't.
            </li>
            <li>
              <b>Your new PMS vendor</b> is responsible for importing the data
              export we generate for you. We provide the export; they ingest
              it.
            </li>
          </ul>

          <p>
            What Asha is responsible for during off-boarding is narrowly
            scoped:{" "}
            <em className="accent">
              generating data exports, decoupling our integrations, and
              participating in the mandatory uninstallation meeting
            </em>
            . Per our Terms of Service (§ 11.3), we are not responsible for any
            costs, delays, claim denials, payment disruptions, or operational
            impacts caused by migration activities run by your incoming
            vendors.
          </p>
        </section>

        {/* § 04 — THE FULL MIGRATION CHECKLIST */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 04</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">
            The migrations that must complete first
          </h2>
          <p className="s-sub">
            Cancellation cannot begin until every migration below — for every
            location on your contract — is complete. The exact list depends on
            which package you're on.
          </p>

          <h3 className="sub">
            Phones <small>VoIP port-out + hardware return</small>
          </h3>
          <p>
            Have your new VoIP provider port your phone numbers off Asha's
            telephony stack and onto theirs. Once your new lines are live in
            your office, <strong>ship the physical phones</strong> we provided
            back to Asha at the return address your Account Manager will
            provide.
          </p>

          <h3 className="sub">
            Payment terminals <small>Processor migration + hardware return</small>
          </h3>
          <p>
            Have your new payment processor migrate your in-office payment
            terminals off Asha's payments stack and onto theirs. Once the new
            terminals are accepting payments in your office,{" "}
            <strong>ship the Asha-provided payment terminals back</strong>.
          </p>

          <h3 className="sub">
            Fax <small>Fax line port-out</small>
          </h3>
          <p>
            Have your new fax provider port your eFax number off Asha onto
            their platform. Fax port-outs are coordinated entirely between you
            and the new provider.
          </p>

          <h3 className="sub">
            Patient messaging <small>Texting number port-out</small>
          </h3>
          <p>
            Have your new patient-messaging provider port your texting / SMS
            number off Asha onto their platform. Your messaging history with
            patients stays with whichever provider holds the number, so plan
            the cutover with your front desk in mind.
          </p>

          <h3 className="sub">
            ERAs <small>Payer-by-payer re-enrollment</small>
          </h3>
          <p>
            You need to <strong>contact every payer one by one</strong> and
            either: (i) re-enroll your ERAs to be delivered to your new
            clearinghouse or vendor, (ii) request a fresh ERA enrollment under
            your new vendor's identifiers, or (iii) convert the payer back to
            paper EOBs. Until every payer on your roster has been re-routed,
            Asha continues to incur enrollment and per-transaction costs on
            your behalf — which is why cancellation doesn't take effect until
            ERA migration is complete (TOS § 11.3(j)).
          </p>

          <h3 className="sub">
            PMS data <small>Only if migrating off the Asha PMS</small>
          </h3>
          <p>
            Submit a written data-export request to Asha. We generate the
            export in standard formats, which typically takes about{" "}
            <strong>seven days</strong>. You then hand that export to your new
            PMS vendor, and <em className="accent">they</em> are responsible
            for importing and validating the data inside their system.
          </p>
          <p>
            Practices on the <strong>Continuous Integration</strong>{" "}
            configuration — where Asha's AI Agents run on top of your existing
            PMS — don't need a PMS data export, since your PMS isn't changing.
            All the other migration steps in this section still apply.
          </p>
        </section>

        {/* § 05 — TIMELINE */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 05</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">The off-boarding timeline</h2>
          <p className="s-sub">
            Off-boarding is sequential by design — the contractual notice
            period only starts running after every migration above is finished.
          </p>

          <dl className="dl">
            <div>
              <dt>
                Talk to your AM
                <small>Step 1</small>
              </dt>
              <dd>
                Surface every concern, file a ticket per issue, and give us a
                fair window to resolve. Most off-boarding conversations end
                here.
              </dd>
            </div>
            <div>
              <dt>
                Sign with new vendors
                <small>Step 2</small>
              </dt>
              <dd>
                Contract with your replacement VoIP provider, payment
                processor, fax provider, patient-messaging provider, and (if
                applicable) PMS vendor.
              </dd>
            </div>
            <div>
              <dt>
                Run all migrations
                <small>Variable — weeks to months</small>
              </dt>
              <dd>
                Each incoming vendor runs its own port-out / data import.
                Payer-by-payer ERA re-enrollment is usually the longest tail
                and often runs in parallel.
              </dd>
            </div>
            <div>
              <dt>
                Ship hardware back
                <small>Once new equipment is live</small>
              </dt>
              <dd>
                Return Asha-provided phones and payment terminals once your
                replacement hardware is in production.
              </dd>
            </div>
            <div>
              <dt>
                Submit the off-boarding packet
                <small>Triggers AM review</small>
              </dt>
              <dd>
                Deliver the complete submission packet (see § 02) to your
                Account Manager. AM reviews for completeness and against our
                off-boarding guidelines within ~5 business days, then either
                accepts it or returns it for missing items.
              </dd>
            </div>
            <div>
              <dt>
                Off-boarding call
                <small>Owner must attend</small>
              </dt>
              <dd>
                Once the packet is accepted, your AM schedules the mandatory
                call with Asha where we safely decouple integrations from your
                environment.
              </dd>
            </div>
            <div>
              <dt>
                Cancellation notice + notice period
                <small>Filed after the call</small>
              </dt>
              <dd>
                Once the off-boarding call is complete, your formal
                cancellation notice is submitted. Your contractual notice
                period — 30 days unless your SOW specifies longer — begins
                from that point.
              </dd>
            </div>
            <div>
              <dt>
                Final fees per contract
                <small>Per your SOW</small>
              </dt>
              <dd>
                Any Early Termination Fee, Discount Clawback, off-boarding
                administrative fee, and the partial-period Monthly Retainer
                through the cancellation effective date are calculated per
                your SOW. See § 08 for what may apply.
              </dd>
            </div>
          </dl>

          <p>
            Per TOS § 11.3, the <strong>cancellation effective date</strong> is
            the latest of: (1) 24 hours after the uninstallation meeting, (2)
            completion of communications migration, and (3) completion of ERA
            re-enrollment. Until that date is reached, fees continue to accrue
            normally.
          </p>
        </section>

        {/* § 06 — UNINSTALLATION MEETING */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 06</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">The uninstallation meeting</h2>
          <p className="s-sub">
            A short, mandatory call to safely decouple Asha from your
            environment. This is not a negotiation meeting — it's a technical
            handoff.
          </p>

          <p>
            Because the Services involve deep integrations with your systems,
            cancellation cannot complete without a structured handoff. The
            uninstallation meeting exists to make sure that handoff is clean
            and that nothing is left in a broken or vulnerable state.
          </p>

          <ul className="simple">
            <li>
              <b>The practice owner must attend.</b> For corporate entities, an
              authorized officer or principal with decision-making authority is
              required. The meeting cannot proceed without them.
            </li>
            <li>
              <b>Give us at least 24 hours' notice to reschedule.</b> A
              No-Show / Late Cancellation Fee of $150 applies for each
              missed-or-late occurrence, including when the owner isn't
              present.
            </li>
            <li>
              <b>Don't try to self-disconnect.</b> Pulling integrations on your
              own — without the uninstallation meeting — can cause data
              vulnerabilities, data loss, and operational disruptions, and
              triggers additional contractual fees on top of anything already
              owed.
            </li>
          </ul>

          <p>
            A flat <strong>$500 off-boarding administrative fee</strong> is
            due at the start of off-boarding (non-refundable). Anything else
            owed at cancellation — Early Termination Fee, Discount Clawback,
            partial-period Monthly Retainer — follows the terms in your
            contract (see § 08).
          </p>
        </section>

        {/* § 07 — MULTI-LOCATION */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 07</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">If you operate multiple locations</h2>
          <p className="s-sub">
            Multi-location off-boarding has to happen{" "}
            <em>across your entire group</em> — not location by location.
          </p>

          <p>
            Per TOS § 11.3(g), if you're a multi-location group, cancellation
            cannot commence until you've completed the full communications
            migration — phones, SMS, and associated infrastructure — away from
            Asha for{" "}
            <strong>every location in your group</strong>, regardless of
            whether those lines are consolidated, shared, or independent.
          </p>

          <p>
            This means you cannot selectively migrate one location and keep
            Asha communications running at others as a way to partially cancel.
            Either every location in your group migrates and you off-board
            entirely, or you continue with Asha. Fees continue to accrue at
            all locations until the migration is complete across the group
            and the cancellation effective date is established.
          </p>
        </section>

        {/* § 08 — FEES & CLAWBACK */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 08</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Fees that may apply at off-boarding</h2>
          <p className="s-sub">
            A quick reference for the financial side of cancellation. Your SOW
            is the source of truth for amounts; this is a plain-English summary.
          </p>

          <dl className="dl">
            <div>
              <dt>
                Off-boarding administrative fee
                <small>Flat fee</small>
              </dt>
              <dd>
                $500 flat. Due at the start of off-boarding and
                non-refundable.
              </dd>
            </div>
            <div>
              <dt>
                No-Show fee
                <small>Uninstallation meeting</small>
              </dt>
              <dd>
                $150 per occurrence when the uninstallation meeting is missed
                or canceled with less than 24 hours' notice, or when the owner
                isn't present.
              </dd>
            </div>
            <div>
              <dt>
                Continued monthly fees
                <small>Through effective date</small>
              </dt>
              <dd>
                Fees accrue normally until the cancellation effective date.
                The Monthly Retainer for the partial billing period in which
                cancellation lands is non-refundable.
              </dd>
            </div>
            <div>
              <dt>
                Early Termination Fee
                <small>If applicable per SOW</small>
              </dt>
              <dd>
                If your SOW specifies an Early Termination Fee and you cancel
                before the end of your Initial Term, it applies. See your SOW
                for the amount.
              </dd>
            </div>
            <div>
              <dt>
                Discount Clawback
                <small>If you took discounted pricing</small>
              </dt>
              <dd>
                If your contract included waived setup fees, a discounted rate,
                or subsidized third-party software, those concessions may be
                clawed back if you terminate mid-term (TOS § 11.7). Capped at
                100% of the remaining-term standard fees.
              </dd>
            </div>
          </dl>

          <p>
            None of the above applies in the cases the TOS expressly carves
            out — for example, if Asha terminates for convenience, if you
            terminate for our uncured material breach, or if you successfully
            exercise a Performance-Based Opt-Out where your SOW provides one.
          </p>
        </section>

        {/* § 09 — SUMMARY */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 09</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Off-boarding at a glance</h2>
          <p className="s-sub">
            The full off-boarding journey in one table.
          </p>

          <table className="summary">
            <thead>
              <tr>
                <th className="col-when">Stage</th>
                <th className="col-detail">What happens</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.when}>
                  <td className="col-when">
                    <div className="sm-when">{row.when}</div>
                  </td>
                  <td className="col-detail">
                    <div className="sm-detail">{row.detail}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* § 10 — FAQ */}
        <section className="s">
          <div className="s-eyebrow">
            <span className="s-num">§ 10</span>
            <span className="s-rule" />
          </div>
          <h2 className="s-title">Frequently asked questions</h2>
          <p className="s-sub">
            Quick answers to the most common questions about off-boarding.
          </p>

          <div className="faq-list">
            <div className="faq-item">
              <h3 className="faq-q">
                If I submit the off-boarding packet today, am I cancelled in
                30 days?
              </h3>
              <p className="faq-a">
                <strong>No.</strong> Submitting the packet starts AM review
                (~5 business days). Even after acceptance, the sequence still
                runs: AM schedules the off-boarding call, the call is
                completed, your formal cancellation notice is then submitted,
                and only at that point does your contractual notice period
                (30 days unless your SOW says otherwise) start running. Per
                TOS § 11.3, the cancellation effective date is the latest of
                (1) 24 hours after the off-boarding call, (2) completion of
                communications migration off Asha, and (3) completion of
                payer-by-payer ERA re-enrollment. Fees continue to accrue
                normally until the effective date.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                Why won't Asha port my phones / payments / faxes / ERAs off?
              </h3>
              <p className="faq-a">
                The same reason your previous provider didn't port them{" "}
                <em>onto</em> Asha when you migrated in — the receiving
                platform is always the one that runs the migration, because
                they're the side with the implementation team and the
                incentive to make sure the new setup works. When you came onto
                Asha, we did the work; when you leave, your new vendors do the
                work. Asha provides data exports and participates in
                decoupling, but we don't operate inside your replacement
                vendors' systems.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                How long does the PMS data export take?
              </h3>
              <p className="faq-a">
                About <strong>seven days</strong> from when you submit a
                written request. We deliver the export in standard formats;
                your new PMS vendor is responsible for importing and
                validating it on their side.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                Can I just disconnect the integrations myself to speed things up?
              </h3>
              <p className="faq-a">
                Please don't. Self-disconnection can cause data
                vulnerabilities, data loss, and operational disruptions because
                of how deeply integrated the Services are. Per TOS § 11.3, if
                you disconnect without completing the uninstallation meeting
                you'll be assessed additional contractual fees on top of
                anything already owed.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                What if I'm having issues — should I just cancel?
              </h3>
              <p className="faq-a">
                <strong>Almost always, no.</strong> In our experience, every
                issue we hear about gets resolved — but we have to{" "}
                <em>hear about it</em>. File a support ticket for each
                individual issue you're experiencing, bring them to your
                Account Manager, and let us work through them with you. Going
                through a full setup again at a new vendor — phones, payments,
                faxes, ERAs, PMS, training — is a much bigger undertaking than
                most cancellation conversations assume.
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-q">
                I'm on Continuous Integration (keeping my existing PMS). Does
                this still apply?
              </h3>
              <p className="faq-a">
                Yes — except for the PMS data export, since your PMS isn't
                changing. You still need to migrate phones, payments, faxes,
                patient messaging, and payer ERAs off Asha before cancellation
                can take effect, and the uninstallation meeting still applies
                to decouple our AI Agents from your PMS.
              </p>
            </div>
          </div>
        </section>

        {/* TAIL */}
        <div className="tail">
          <span>Asha Health, Inc.</span>
          <span>Confidential to recipient</span>
          <span>www.asha.health · sales@asha.health</span>
        </div>
        </div>
      </div>
    </>
  );
};

export default OffboardingGuidePage;
